import React, {
  CSSProperties,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Input, Popover } from 'antd';
import styles from './style.less';
import { PopoverProps } from 'antd/es/popover';
import { playSound } from '@/utils/sound';
import CustomIcon from '../CustomIcon';
import { InputProps } from 'antd/es/input';
import { IS_POS } from '@/utils/env';
import { addGlobalScanListener, removeGlobalScanListener } from '../QRScanner/GlobalScanner';
import { debounce, isNumber } from '@/utils/common';
import { InputNumberProps } from 'antd/es/input-number';
import ScannerInput from './ScannerInput';

// prettier-ignore
const ARRAYS_KEYBOARD_ALPHABET = [
  `1`, `2`, `3`, `4`, `5`, `6`, `7`, `8`, `9`, `0`,
  `Q`, `W`, `E`, `R`, `T`, `Y`, `U`, `I`, `O`, `P`,
  `A`, `S`, `D`, `F`, `G`, `H`, `J`, `K`, `L`,
  `Z`, `X`, `C`, `V`, `B`, `N`, `M`
];

export type NumericKeyboardProps = {
  children?: React.ReactNode;
  onComplete?: (v: number | string) => void;
  onChange?: (v: number | string) => void;
  value?: number | string;
  itemHeight?: number;
  style?: CSSProperties;
  className?: string;
  placeholder?: string;
  precision?: number;
  autoFocus?: boolean;
  showMonitor?: boolean;
  clearValueOnClose?: boolean;
  showAllClean?: boolean;
  showConfirm?: boolean;
  closeOnScanReceived?: boolean;
  parseValueToNumber?: boolean;
  maxLength?: number;
  closeOnMaxLength?: boolean;
  playSoundExcludes?: ('enter' | 'backspace' | 'close')[];
  mode?: 'number' | 'numberAndAlphabet';
} & PopoverProps;

const NumericKeyboard: React.FC<NumericKeyboardProps> = ({
  autoFocus = true,
  children,
  onComplete = () => {},
  onChange: onChangeProp = () => {},
  value: valueProp,
  open: openProp,
  itemHeight = 60,
  placeholder = '请输入',
  className = '',
  style = {},
  precision = 0,
  showMonitor = true,
  clearValueOnClose = true,
  showAllClean = false,
  showConfirm = true,
  closeOnScanReceived = false,
  parseValueToNumber = false,
  onOpenChange = () => {},
  playSoundExcludes = [],
  maxLength = Number.MAX_VALUE,
  closeOnMaxLength = false,
  mode = `number`,
  ...popoverProps
}: NumericKeyboardProps) => {
  const flatMode = React.Children.count(children) === 0;
  const containerRef = useRef<HTMLDivElement>();
  const inputRef = useRef<HTMLInputElement>();
  const [open, setOpen] = useState(flatMode);
  const [value, setValue] = useState<string>('');
  const playSoundOnClick = useCallback(
    // debounce(() => {
    //   playSound('click');
    // }, +localStorage.getItem(STORAGE_KEY_DEVICE_PARAM_SCANNER_DELAY)),
    debounce(key => {
      if (!playSoundExcludes.includes(key)) {
        playSound('click');
      }
    }, 30),
    [playSoundExcludes]
  );

  // valueProp -> value
  useEffect(() => {
    if (!isNaN(valueProp as number)) {
      if (parseValueToNumber) {
        if (+valueProp !== +value) {
          setValue(`${valueProp}`);
        }
      } else {
        setValue(`${valueProp}`);
      }
    } else {
      setValue('');
    }
  }, [valueProp, parseValueToNumber]);

  // visibleProp -> visible
  useEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  const close = () => {
    if (!flatMode) {
      // setVisible(false);
      if (clearValueOnClose && valueProp === undefined) {
        setValue('');
      }
    }
  };

  // 扫码枪识别关闭
  useEffect(() => {
    if (closeOnScanReceived) {
      const key = addGlobalScanListener('all', () => {
        setOpen(false);
      });
      return () => {
        removeGlobalScanListener(key);
      };
    }
  }, [closeOnScanReceived]);

  useEffect(() => {
    if (!open) {
      close();
    }
  }, [open]);

  const onChange = (valueStr: string) => {
    if (valueStr.indexOf('.') > -1) {
      // 有小数点
      const [integer, fractional] = valueStr.split('.');
      if (fractional.length > precision) {
        // 位数超过 precision, 用最后一位替代倒数第二位
        valueStr = `${integer}.${fractional.substr(0, fractional.length - 2)}${fractional.substr(
          -1
        )}`;
      }
    }

    if (valueStr.length > maxLength) {
      return;
    }

    setValue(valueStr);
    if (valueStr.length) {
      if (parseValueToNumber) {
        if (+valueStr !== +valueProp) {
          onChangeProp(+valueStr);
        }
      } else {
        onChangeProp(valueStr);
      }
    } else {
      onChangeProp(undefined);
    }

    if (closeOnMaxLength && valueStr.length === maxLength) {
      setOpen(false);
    }
  };

  const onClick = (v: string) => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }

    // precision 为 0 禁止输入小数点
    if (v === '.') {
      if (precision === 0) {
        return;
      }
    }

    const activeElem = containerRef.current.querySelector(`[aria-label="${v}"]`);
    playSoundOnClick(v);

    if (activeElem) {
      activeElem.classList.add(styles.active);
      setTimeout(() => {
        activeElem.classList.remove(styles.active);
      }, 100);
    }

    if (v === '.') {
      onChange(value.replace('.', '') + v);
    } else if (v === 'backspace') {
      if (value.length) {
        onChange(value.substr(0, value.length - 1));
      }
    } else if (v === 'ac') {
      onChange('');
    } else if (v === 'enter') {
      if (parseValueToNumber) {
        onComplete(+value);
      } else {
        onComplete(value);
      }
      setOpen(false);
    } else if (v === 'close') {
      setOpen(false);
      playSoundOnClick('close');
    } else {
      onChange(value + v);
    }
  };

  useEffect(() => {
    if (autoFocus && open && inputRef && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus({ preventScroll: true });
      });
    }
  }, [open, autoFocus]);

  const main = (
    <div className={`${styles.container} ${className}`} ref={containerRef} style={style}>
      {showMonitor && (
        <div className={styles.monitor}>
          <ScannerInput
            className={styles.noSelect}
            autoFocus
            placeholder={placeholder}
            ref={inputRef}
            value={value}
            onChange={() => {}}
            onKeyDown={({ key }) => {
              key = key.toLowerCase();
              if (key === 'backspace' || key === 'enter' || key === '.') {
                onClick(key);
              } else if (/^[\d\w]$/.test(key)) {
                if (mode === 'number') {
                  if (!isNaN(+key)) {
                    onClick(key);
                  }
                } else {
                  onClick(key.toUpperCase());
                }
              }
            }}
          />
        </div>
      )}
      <div className={`${styles.keyboard} ${styles[mode]}`}>
        <div className={styles.nums}>
          {mode === `number` && (
            <>
              {Array.from(Array(9).keys()).map(i => {
                const num = i + 1;
                return (
                  <div
                    style={{ height: itemHeight }}
                    aria-label={`${num}`}
                    className={styles.item}
                    key={num}
                    onClick={() => {
                      onClick(`${num}`);
                    }}>
                    {num}
                  </div>
                );
              })}
              <div
                style={{ height: itemHeight }}
                aria-label={`.`}
                className={`${styles.item} ${precision === 0 ? styles.disabled : ''}`}
                onClick={() => {
                  onClick('.');
                }}>
                .
              </div>
              <div
                style={{ height: itemHeight }}
                aria-label={`0`}
                className={styles.item}
                onClick={() => {
                  onClick('0');
                }}>
                0
              </div>
            </>
          )}
          {mode === `numberAndAlphabet` &&
            ARRAYS_KEYBOARD_ALPHABET.map(alphabet => {
              let marginLeft = undefined;
              if (alphabet === 'A') {
                marginLeft = 35;
              }
              if (alphabet === 'Z') {
                marginLeft = 70;
              }
              return (
                <div
                  style={{ height: itemHeight, marginLeft }}
                  aria-label={`${alphabet}`}
                  className={styles.item}
                  key={alphabet}
                  onClick={() => {
                    onClick(`${alphabet}`);
                  }}>
                  {alphabet}
                </div>
              );
            })}

          <div
            style={{ height: itemHeight }}
            className={`${styles.item} ${flatMode ? styles.disabled : ''}`}
            onClick={() => {
              onClick('close');
            }}>
            <CustomIcon type={'keyboard-collapse'} />
          </div>
        </div>
        <div className={styles.actions}>
          <div
            aria-label={`backspace`}
            className={styles.del}
            onClick={() => {
              onClick('backspace');
            }}>
            <CustomIcon type={'backspace'} />
          </div>
          {showAllClean && (
            <div
              aria-label={`ac`}
              className={styles.ac}
              onClick={() => {
                onClick('ac');
              }}>
              AC
            </div>
          )}
          {showConfirm && (
            <div
              aria-label={`enter`}
              className={styles.confirm}
              onClick={() => {
                onClick('enter');
              }}>
              确定
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (flatMode) {
    return main;
  }

  return (
    <>
      <Popover
        overlayClassName={styles.popover}
        getPopupContainer={triggerNode => {
          const modal = triggerNode.closest('.ant-modal-content');
          if (modal) {
            return modal as HTMLElement;
          }
          return document.body;
        }}
        {...popoverProps}
        open={open}
        onOpenChange={v => {
          if (onOpenChange) {
            onOpenChange(v);
          }
          setOpen(v);
        }}
        content={main}>
        {children}
      </Popover>
    </>
  );
};

export default NumericKeyboard;

type NumericKeyboardInputProp = {
  value?: number | string;
  onChange?: (v: number | string) => void;
  keyboardProps?: NumericKeyboardProps;
  emptyValueOnOpen?: boolean;
  inputType?: React.ElementType;
  inputProps?: InputProps & InputNumberProps & {};
};
export const NumericKeyboardInput: React.FC<NumericKeyboardInputProp> = ({
  value: valueProp,
  onChange,
  keyboardProps = {},
  emptyValueOnOpen = false,
  inputProps = {},
  inputType = Input,
}: NumericKeyboardInputProp) => {
  const [value, setValue] = useState<number | string>();

  if (!IS_POS) {
    return React.createElement(inputType, {
      ...inputProps,
      value: valueProp,
      onChange: e => {
        if (e && e.target) {
          onChange(e.target.value);
        } else {
          onChange(e);
        }
      },
    });
  }

  useEffect(() => {
    if (isNumber(+valueProp)) {
      setValue(valueProp);
    }
  }, [valueProp]);

  return (
    <NumericKeyboard
      {...keyboardProps}
      placeholder={inputProps.placeholder || inputProps.placeholder}
      value={emptyValueOnOpen ? undefined : value}
      trigger={'click'}
      onChange={v => {
        onChange(v as number);
        if (isNumber(v)) {
          if (inputProps.max) {
            onChange(Math.min(+inputProps.max, +v));
          }

          if (inputProps.min) {
            onChange(Math.max(+inputProps.min, +v));
          }
        }
        setValue(v as number);
      }}>
      {/*<Input*/}
      {/*  {...inputProps}*/}
      {/*  className={`${styles.noSelect} ${inputProps.className || ''}`}*/}
      {/*  readOnly*/}
      {/*  value={value}*/}
      {/*/>*/}
      {React.createElement(inputType, {
        ...inputProps,
        readOnly: true,
        value: valueProp,
        className: `${styles.noSelect} ${inputProps.className || ''}`,
        onChange: e => {
          if (e.target) {
            onChange(e.target.value);
          } else {
            onChange(e);
          }
        },
      })}
    </NumericKeyboard>
  );
};

type IsolateKeyboardProp = {
  value?: string;
  onChange?: (v: string) => void;
  keyboardProps?: NumericKeyboardProps;
  emptyValueOnOpen?: boolean;
  showMask?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

export const IsolateKeyboard: React.FC<IsolateKeyboardProp> = ({
  showMask = false,
  value: valueProp,
  onChange,
  emptyValueOnOpen = false,
  keyboardProps = {},
  inputProps = {},
}: IsolateKeyboardProp) => {
  const valueArr = (valueProp || '').split('');
  return (
    <NumericKeyboard
      {...keyboardProps}
      placeholder={inputProps.placeholder || inputProps.placeholder}
      value={emptyValueOnOpen ? undefined : valueProp}
      trigger={'click'}
      clearValueOnClose={false}
      closeOnMaxLength
      onChange={v => {
        onChange(v as string);
      }}>
      <div className={styles.isolateKeyboardWrapper}>
        {Array.from(Array(6).keys()).map(i => {
          const value = valueArr[i] || '';
          return (
            <div className={styles.item} key={i}>
              {showMask ? value.length ? <i>●</i> : '' : value}
            </div>
          );
        })}
      </div>
    </NumericKeyboard>
  );
};
